import { useCallback, useRef, useState } from 'react'
import { type ChartOptions, HighchartsReactRefObject } from '@highcharts/react'
import { Grid, useTheme } from '@mui/material'

import BaseChart from './base-chart'
import { DEFAULT_CHART_TIME_RANGE, defaultDataProps, extractSequences, getAlertPlotLines, rangeSelectorIndexMatrix, useFetchChartData } from './helpers'
import classes from './index.styles'

const BinaryChart = ({
  title,
  deviceId,
  channel,
  chart,
  alerts,
}: {
  title: string
  deviceId: number
  channel: ChannelType
  chart: string
  alerts: IAlertHistory[]
}) => {
  const theme = useTheme()
  const chartRef = useRef<HighchartsReactRefObject>(null)
  const [selectedRange, setSelectedRange] = useState<ChartTimeRange>(DEFAULT_CHART_TIME_RANGE)
  const { data, isLoading, fetchData } = useFetchChartData(deviceId, [channel], chart)
  const plotLines = getAlertPlotLines({ alerts, channels: [channel], theme })

  const seriesData = data[0]?.data && data[0]?.data.length > 0 ? extractSequences(data[0].data) : []

  // Update selected range immediately for button state, then load the corresponding data
  const onRangeBtnClick = useCallback(
    async (timeRange: ChartTimeRange) => {
      setSelectedRange(timeRange)
      chartRef?.current?.chart.showLoading()
      await fetchData(timeRange)
    },
    [fetchData]
  )

  const chartData = {
    ...defaultDataProps,
    title: {
      ...defaultDataProps.title,
      text: title,
    },
    accessibility: {
      description: `${title} chart.`,
    },
    chart: {
      type: 'xrange',
      height: 400,
      spacingLeft: 48,
      events: {
        render: () => {
          const hcChart = chartRef?.current?.chart as
            (HighchartsReactRefObject['chart'] & { rangeSelector?: { selected: number; buttons?: { setState: (state: number) => void }[] } }) | undefined
          hcChart?.hideLoading()

          // Highcharts computes the active range-selector button from current x-axis extremes.
          // Our selectedRange is a UI/data filter that may not match those extremes exactly,
          // so we manually keep the button highlight in sync with selectedRange.
          const rs = hcChart?.rangeSelector
          const targetIndex = rangeSelectorIndexMatrix[selectedRange]
          if (rs && rs.selected !== targetIndex) {
            rs.buttons?.[rs.selected]?.setState(0)
            rs.buttons?.[targetIndex]?.setState(2)
            rs.selected = targetIndex
          }
        },
      },
    },
    colors: ['#2D9CDB'],
    rangeSelector: {
      allButtonsEnabled: true,
      selected: rangeSelectorIndexMatrix[selectedRange],
      buttons: [
        {
          type: 'day',
          count: 1,
          text: '1d',
          title: 'View 1 day',
          events: {
            click: () => onRangeBtnClick('day'),
          },
        },
        {
          type: 'week',
          count: 1,
          text: '1w',
          title: 'View 1 week',
          events: {
            click: () => onRangeBtnClick('week'),
          },
        },
        {
          type: 'month',
          count: 1,
          text: '1m',
          title: 'View 1 month',
          events: {
            click: () => onRangeBtnClick('month'),
          },
        },
      ],
    },
    yAxis: {
      title: {
        text: '',
      },
      categories: [data[0]?.seriesName],
      minorGridLineWidth: 0,
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
    },
    xAxis: {
      crosshair: true,
      plotLines,
    },
    plotOptions: {
      series: {
        dataGrouping: {
          enabled: false,
        },
      },
    },
    navigator: {
      series: {
        type: 'xrange',
        pointWidth: 10,
      },
    },
    series: [
      {
        name: data[0]?.seriesName,
        type: 'xrange',
        data: seriesData,
        turboThreshold: seriesData.length,
        pointWidth: 20,
        showInNavigator: true,
        showInLegend: true,
        dataLabels: {
          enabled: true,
        },
        legendColor: '#2D9CDB',
        tooltip: {
          headerFormat: '<span style="font-size: 0.8em">{point.x:%b %e %Y %l:%M%P} - {point.x2:%b %e %Y %l:%M%P} (UTC)</span><br/>',
          pointFormat: '<span style="color:{point.color}">●</span> {point.yCategory}<br/>',
        },
      },
    ],
  }
  return (
    <Grid size={12} sx={classes.baseChartContainer}>
      <BaseChart chartData={chartData as unknown as ChartOptions} chartRef={chartRef} isLoading={isLoading} />
    </Grid>
  )
}

export default BinaryChart
